import { useState } from 'react'

import { Card, CardContent } from '@/app/common/components/Card'
import { RequestError } from '@/app/common/types_and_interfaces.ts'
import {
  ApplicationStatus,
  FamilyApplication,
} from '@/queries/family_applications/requests'
import { useToast } from '@/shad/ui/toast/use-toast'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { useSelector } from 'react-redux'
import { currencyFormatter } from '@/lib/utils'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import { useFundAwardedStudents } from '@/queries/program_purses/useFundAwardedStudents.ts'
import { getFundAwardedStudentsErrorMessage } from '../../fund-awarded-students/fund-awarded-students-utils'
import { StepIndicator } from './_components/step-indicator'
import ConfirmAwardStep from './confirm-award-step/confirm-award-step'
import SelectPurseStep from './select-purse-step/select-purse-step'
import { SelectStudentsStepForm } from './select-students-step/select-students-step.form'
import PageLoader from '@/app/common/components/PageLoader'
import { useGetStudentAwardPreview } from './hooks/useGetStudentAwardPreview.tsx'
import { getDisplayedStudentStatus } from '../student-management-util.ts'
import { buildPurseAwardOptions } from './award-form-utils.ts'

interface AwardDialogProps {
  onOpenChange: (open: boolean) => void
  selectedStudents: SelectedStudents
  purses: Purse[]
  onAwardComplete: (purseId: number, studentIds: number[]) => void
  familyApplications: FamilyApplication[]
}

export type AwardFormSteps =
  | 'select_purse'
  | 'review_students'
  | 'confirm_award'

export type SelectedStudents = { studentId: number }[]

export type StudentAwardInformation = {
  id: number | null
  first_name: string | null
  last_name: string | null
  amount_to_be_awarded: number
  is_recieving_additional_award: boolean
  student_grade: string | null | undefined
  applciant_email: string | null
  status: ApplicationStatus
  total_awarded_amount: number
  /** Whether the selected purse appears in this student's purse associations. */
  is_associated_with_purse: boolean
  /** Whether ServiceNow sent an award amount for this student on the selected purse. */
  has_award_amount: boolean
}

export function AwardDialog({
  onOpenChange,
  selectedStudents,
  purses,
  onAwardComplete,
  familyApplications,
}: AwardDialogProps) {
  const [currentStep, setCurrentStep] = useState<AwardFormSteps>('select_purse')
  const [selectedPurseId, setSelectedPurseId] = useState('')
  const [confirmationText, setConfirmationText] = useState('')

  const programId = useSelector(selectSelectedProgram).id
  const { toast } = useToast()

  const selectedPurse = purses.find((p) => p.id.toString() === selectedPurseId)

  const studentIds = selectedStudents
    .map((student) => student.studentId)
    .filter((id): id is number => id != null)

  // Every purse any selected student is associated with, so the list stays limited to purses
  // relevant to this batch without requiring all of them to share one.
  const selectedStudentPurseIds = new Set(
    studentIds.flatMap((studentId) => {
      const studentInfo = familyApplications.find(
        (app) => app.student_id === studentId
      )
      return (studentInfo?.program_purses ?? []).map((purse) => purse.id)
    })
  )

  const selectablePurses = purses.filter((purse) =>
    selectedStudentPurseIds.has(purse.id)
  )

  // Priced up front for every candidate purse, so the purse step can grey out the ones that cannot
  // fund this batch and the review step can reuse the same response instead of refetching per purse.
  const shouldFetchPreview =
    studentIds.length > 0 && selectablePurses.length > 0

  const awardPreviewQuery = useGetStudentAwardPreview({
    programId,
    studentIds,
    programPurseIds: selectablePurses.map((purse) => purse.id),
    enabled: shouldFetchPreview,
  })

  const purseAwardOptions = buildPurseAwardOptions({
    purses: selectablePurses,
    purseAwards: awardPreviewQuery.data?.purse_awards,
  })

  const selectedPurseAward = awardPreviewQuery.data?.purse_awards.find(
    (award) => award.program_purse_id === selectedPurse?.id
  )

  function getStudentInformation(): StudentAwardInformation[] {
    const awardedAmountByStudentId = new Map<number, number>()
    selectedPurseAward?.students.forEach((student) => {
      awardedAmountByStudentId.set(student.student_id, student.amount_awarded)
    })

    return selectedStudents.map((student) => {
      const studentInfo = familyApplications.find(
        (app) => app.student_id === student.studentId
      )

      if (!studentInfo) {
        throw new Error('Student not found')
      }

      const awardedAmount =
        studentInfo.student_id != null
          ? awardedAmountByStudentId.get(studentInfo.student_id)
          : undefined

      const displayStatus = getDisplayedStudentStatus(studentInfo)

      const isRecievingAdditionalAward = displayStatus === 'Funded'

      return {
        id: studentInfo.student_id,
        first_name: studentInfo.student_first_name,
        amount_to_be_awarded: awardedAmount ?? 0,
        last_name: studentInfo.student_last_name,
        student_grade: studentInfo.student_grade,
        status: displayStatus,
        is_recieving_additional_award: isRecievingAdditionalAward,
        applciant_email: studentInfo.applicant_email,
        total_awarded_amount: studentInfo.total_awarded_amount || 0,
        is_associated_with_purse: (studentInfo.program_purses ?? []).some(
          (purse) => purse.id === selectedPurse?.id
        ),
        has_award_amount: awardedAmount != null && awardedAmount > 0,
      }
    })
  }

  const studentInformation = getStudentInformation()

  // Award students mutation
  // Shared with the per purse action, so cache invalidation (purses, purse details, transaction
  // logs, applicant lists, awarded students preview) stays in one place.
  const fundStudentsMutation = useFundAwardedStudents({
    onSuccess: (data) => {
      const fundedCount = data.student_purses.length

      toast({
        variant: 'default',
        title: fundedCount > 0 ? 'Students Funded' : 'Nothing to Fund',
        description:
          fundedCount > 0
            ? `Funded ${fundedCount} student${fundedCount !== 1 ? 's' : ''} for ${currencyFormatter.format(
                data.total_awarded
              )} from the "${selectedPurse?.name}" purse.`
            : `None of the selected students were waiting to be funded on the "${selectedPurse?.name}" purse.`,
      })

      if (selectedPurse) {
        onAwardComplete(selectedPurse.id, studentIds)
      }

      onOpenChange(false)
      resetForm()
    },
    onError: (error: RequestError) => {
      toast({
        variant: 'destructive',
        title: 'Funding Failed',
        description: getFundAwardedStudentsErrorMessage(
          error,
          selectedPurse?.name ?? ''
        ),
      })
    },
  })

  const handlePurseSelect = (purseId: string) => {
    setSelectedPurseId(purseId)
  }

  const handleConfirmAward = () => {
    // An empty `studentIds` makes the API fund every Awarded student on the purse, so a batch that
    // lost its students must never reach the mutation.
    if (!selectedPurse || studentIds.length === 0) return
    fundStudentsMutation.mutate({
      programId,
      purseId: selectedPurse.id,
      studentIds,
    })
  }

  const resetForm = () => {
    setCurrentStep('select_purse')
    setSelectedPurseId('')
    setConfirmationText('')
  }

  const isConfirmationValid =
    confirmationText.trim() === selectedPurse?.name?.trim()
  const getStepContent = () => {
    if (currentStep === 'select_purse') {
      const selectPurseStepProps = {
        onCancelClick: () => {
          onOpenChange(false)
        },
        onNext: () => {
          setCurrentStep('review_students')
        },
        purseOptions: purseAwardOptions,
        selectedPurseId,
        onPurseSelect: handlePurseSelect,
      }

      if (!shouldFetchPreview) {
        return <SelectPurseStep {...selectPurseStepProps} />
      }

      return (
        <PageLoader query={awardPreviewQuery}>
          {() => <SelectPurseStep {...selectPurseStepProps} />}
        </PageLoader>
      )
    }

    if (!selectedPurse) {
      throw new Error('Selected purse not found')
    }

    if (currentStep === 'review_students') {
      // The preview was already resolved on the purse step, so this step reads the cached response
      // instead of issuing a second request.
      return (
        <SelectStudentsStepForm
          purse={selectedPurse}
          selectedStudents={studentInformation}
          onNextClick={() => setCurrentStep('confirm_award')}
          onBackClick={() => setCurrentStep('select_purse')}
        />
      )
    }

    return (
      <ConfirmAwardStep
        onBackClick={() => {
          setCurrentStep('review_students')
        }}
        selectedPurse={selectedPurse}
        selectedStudents={studentInformation}
        confirmationText={confirmationText}
        onConfirmationTextChange={setConfirmationText}
        isConfirmationValid={isConfirmationValid}
        onConfirmAward={handleConfirmAward}
        isSubmitting={fundStudentsMutation.isPending}
      />
    )
  }

  return (
    <>
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-0">
          <div className="bg-gray-50 border-b py-4 rounded-t-lg border-gray-200">
            <StepIndicator currentStep={currentStep} />
          </div>
          <div className="py-4 px-4">{getStepContent()}</div>
        </CardContent>
      </Card>
    </>
  )
}
